/**
 * One entry in full, fetched when its row expands: three boxes side by side
 * (stacked on a narrow screen), or the Request box alone when the action
 * recorded no documents. Request holds every property of the entry,
 * the reason for a refusal or failure first; Before and After hold the
 * documents with changed fields highlighted (the Atlas f1.5 detail panel).
 */

import { AlertTriangle, RotateCw } from 'lucide-react';

import { useAuditEntry } from '@/api/queries';
import type { AuditEntryDetail as Entry } from '@/api/types';

import { type Doc, entryDocs, renderJson } from './auditDiff';
import { formatAuditTime } from './auditTime';

const REFUSAL: Record<'DENIED' | 'FAILED', { className: string; label: string }> = {
  DENIED: { className: 'badge-warning', label: 'Denied' },
  FAILED: { className: 'badge-danger', label: 'Failed' },
};

function Value({ value }: { value: unknown }) {
  if (value === undefined) return <span className="diff-absent">absent</span>;
  if (value === '[REDACTED]') return <span className="diff-redacted">[REDACTED]</span>;
  return <>{renderJson(value)}</>;
}

function Pane({
  side,
  title,
  doc,
  changed,
  empty,
}: {
  side: 'before' | 'after';
  title: string;
  doc: Doc | null;
  changed: Set<string>;
  /** What an absent document means for this entry. */
  empty: string;
}) {
  return (
    <div className="diff-pane">
      <div className={`diff-head ${side}`}>{title}</div>
      <div className="diff-body">
        {doc === null ? (
          <div className="diff-absent">{empty}</div>
        ) : (
          Object.keys(doc).map((field) => (
            <div key={field}>
              <span className="k">{field}:</span>{' '}
              <span className={changed.has(field) ? 'changed' : undefined}>
                <Value value={doc[field]} />
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Prop({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="audit-prop">
      <span className="k">{label}</span>
      {value === null || value === undefined || value === '' ? (
        <span className="diff-absent">not recorded</span>
      ) : (
        <span>{value}</span>
      )}
    </div>
  );
}

function RefusalReason({ e }: { e: Entry }) {
  const refusal = REFUSAL[e.outcome as 'DENIED' | 'FAILED'];
  return (
    <div className="audit-prop audit-reason">
      <span className={`badge ${refusal.className}`}>{refusal.label}</span>
      <span>{e.reason ?? 'No reason recorded'}</span>
    </div>
  );
}

function RequestPane({ e, context, utc }: { e: Entry; context: Doc | null; utc: boolean }) {
  const refused = e.outcome !== 'SUCCESS';
  return (
    <div className="diff-pane">
      <div className="diff-head request">Request</div>
      <div className="diff-body">
        {refused && <RefusalReason e={e} />}
        <Prop label="When" value={`${formatAuditTime(e.occurred_at, utc)}${utc ? ' UTC' : ''}`} />
        <Prop label="Actor" value={e.actor_display} />
        {e.via && <Prop label="Via" value={e.via} />}
        <Prop label="Actor ID" value={e.actor_user_id} />
        <Prop label="Role" value={e.actor_role_at_time} />
        <Prop label="Action" value={e.action} />
        <Prop label="Family" value={e.event_family} />
        <Prop label="Entity" value={e.entity_type} />
        <Prop label="Entity ID" value={e.entity_id} />
        <Prop label="Outcome" value={e.outcome} />
        <Prop label="Status" value={e.status_code ?? (refused ? null : 'committed')} />
        <Prop label="Request" value={`${e.http_method} ${e.route_template}`} />
        <Prop label="Request ID" value={e.request_id} />
        <Prop label="Source IP" value={e.source_ip} />
        <Prop label="User agent" value={e.user_agent} />
        {!refused && e.reason && <Prop label="Reason" value={e.reason} />}
        {context && Object.entries(context).map(([k, v]) => <Prop key={k} label={k} value={renderJson(v)} />)}
      </div>
    </div>
  );
}

function EntryBoxes({ e, utc }: { e: Entry; utc: boolean }) {
  const { context, before, after, recorded, changed } = entryDocs(e);
  // An action that keeps no documents (a sign-in, a read) would show two
  // identical empty boxes; the Request alone says everything there is.
  if (!recorded) {
    return (
      <div className="detail-panel">
        <div className="audit-boxes is-request-only">
          <RequestPane e={e} context={context} utc={utc} />
        </div>
      </div>
    );
  }
  return (
    <div className="detail-panel">
      <div className="audit-boxes">
        <RequestPane e={e} context={context} utc={utc} />
        <Pane side="before" title="Before" doc={before} changed={changed} empty="Did not exist" />
        <Pane side="after" title="After" doc={after} changed={changed} empty="No longer exists" />
      </div>
    </div>
  );
}

export function AuditEntryDetail({ entryId, utc }: { entryId: string; utc: boolean }) {
  const entry = useAuditEntry(entryId);

  if (entry.isPending) {
    return (
      <div className="detail-panel detail-skeleton" aria-busy="true">
        <span className="skeleton-bar" />
      </div>
    );
  }
  if (entry.error) {
    return (
      <div className="detail-note detail-note-warning">
        <AlertTriangle aria-hidden="true" />
        <span>{entry.error.message}</span>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => void entry.refetch()}>
          <RotateCw aria-hidden="true" /> Retry
        </button>
      </div>
    );
  }
  return <EntryBoxes e={entry.data} utc={utc} />;
}
